import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

// ===== CONSTANTS =====
const FADE_UP = {
  hidden: { opacity: 0, y: 25 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: 'easeOut', delay: i * 0.08 },
  }),
};

const SOCIAL_ICONS = [
  {
    label: 'WhatsApp',
    href: 'https://wa.me/919234804849',
    icon: (
      <svg viewBox="0 0 32 32" className="w-4 h-4" fill="currentColor">
        <path d="M16.01 3.2c-7.07 0-12.8 5.73-12.8 12.8 0 2.25.58 4.44 1.69 6.37L3.2 28.8l6.59-1.67a12.76 12.76 0 0 0 6.22 1.61h.01c7.06 0 12.79-5.73 12.79-12.8 0-3.42-1.33-6.64-3.76-9.06A12.7 12.7 0 0 0 16.01 3.2zm0 23.38h-.01a10.6 10.6 0 0 1-5.4-1.47l-.39-.23-3.91.99 1.04-3.82-.25-.4a10.62 10.62 0 0 1-1.62-5.7c0-5.88 4.77-10.66 10.65-10.66 2.84 0 5.52 1.11 7.53 3.12a10.57 10.57 0 0 1 3.11 7.53c0 5.88-4.78 10.64-10.65 10.64zm5.84-8.06c-.32-.16-1.89-.94-2.18-1.05-.29-.11-.5-.16-.71.16-.21.32-.82 1.05-1.01 1.27-.19.21-.38.24-.7.08-.32-.16-1.35-.49-2.57-1.57-.95-.84-1.59-1.88-1.78-2.2-.19-.32-.02-.49.14-.65.14-.14.32-.38.48-.56.16-.19.21-.32.32-.54.11-.21.05-.4-.03-.56-.08-.16-.71-1.72-.98-2.36-.25-.61-.5-.52-.7-.53h-.6c-.21 0-.56.08-.85.4s-1.12 1.09-1.12 2.66 1.15 3.08 1.31 3.3c.16.21 2.26 3.45 5.48 4.83.77.33 1.37.52 1.84.66.77.24 1.46.2 2.01.12.61-.09 1.89-.77 2.16-1.51.27-.75.27-1.39.19-1.52-.08-.13-.29-.21-.61-.37z" />
      </svg>
    ),
    hoverBg: 'hover:bg-[#25D366] hover:text-white hover:border-[#25D366]',
  },
  {
    label: 'Email',
    href: 'mailto:arvihharinfo@gmail.com',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    hoverBg: 'hover:bg-[#EF4444] hover:text-white hover:border-[#EF4444]',
  },
  {
    label: 'Call Direct',
    href: 'tel:9234804849',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    hoverBg: 'hover:bg-[#1976D2] hover:text-white hover:border-[#1976D2]',
  },
];

// Cleaned & correctly ordered Quick Links matching active landing sections
const QUICK_LINKS = [
  { label: 'Home', href: '#slideshow' },
  { label: 'Core Services', href: '#housing-services' },
  { label: 'Careers & Vacancies', href: '#career' },
  { label: 'Company Profile & Leadership', href: '#director-details' },
  { label: 'Testimonials', href: '#testimonials' },
  { label: 'Get In Touch', href: '#contact' },
];

const SectionHeader = ({ children }) => (
  <h4 className="text-[#001726] text-xs font-bold tracking-[0.2em] uppercase mb-5 flex items-center gap-2.5">
    <span
      className="w-5 h-0.5 rounded-full"
      style={{ background: 'linear-gradient(90deg, #1976D2, #6FCEEC)' }}
    />
    {children}
  </h4>
);

const Footer = () => {
  const footerRef = useRef(null);
  const isInView = useInView(footerRef, { once: true, margin: '-60px' });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) controls.start('visible');
  }, [isInView, controls]);

  const handleSmoothScroll = (e, targetHref) => {
    e.preventDefault();
    const id = targetHref.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer
      ref={footerRef}
      className="relative bg-gradient-to-b from-[#F4FBFE] via-[#FFFFFF] to-[#F4FBFE] text-[#001726] pt-16 pb-8 px-6 overflow-hidden border-t border-[#2C61B924]"
    >
      {/* Matching crisp dot grid overlay */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: 'radial-gradient(#1976D2 0.75px, transparent 0.75px)',
          backgroundSize: '24px 24px',
        }}
      />

      {/* Ambient background glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[260px] rounded-full blur-[130px] pointer-events-none opacity-30"
        style={{
          background: 'radial-gradient(circle, #6FCEEC 0%, #1976D2 50%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial="hidden"
          animate={controls}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.1 } },
          }}
          className="grid lg:grid-cols-4 md:grid-cols-2 gap-10 lg:gap-12 mb-14"
        >
          {/* COLUMN 1: Logo & Company Description */}
          <motion.div variants={FADE_UP} custom={0} className="relative">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={process.env.PUBLIC_URL + '/images/logo.png'}
                alt="Aryavihhar Security Services"
                className="h-11 w-auto drop-shadow-sm"
              />
              <div>
                <p className="text-[#1976D2] font-extrabold text-[1.05rem] leading-tight m-0">Aryavihhar</p>
                <p className="text-[#64748B] text-xs font-semibold m-0 tracking-wide">Growing Together & Protecting You...</p>
              </div>
            </div>

            <p className="text-[#475569] text-sm leading-[1.7] max-w-xs font-normal mb-5">
              ARYAVIHHAR INTELLIGENCE AND SECURITY SERVICES PVT. LTD. is a licensed and certified security agency based in Patna, Bihar, offering reliable protection, facility management, and IT services.
            </p>

            {/* Social / Connect Icons */}
            <div className="flex items-center gap-2.5">
              {SOCIAL_ICONS.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className={`w-9 h-9 rounded-xl bg-white border border-[#2C61B924] flex items-center justify-center text-[#1976D2] transition-all duration-200 shadow-sm hover:scale-105 ${item.hoverBg}`}
                  aria-label={item.label}
                  title={item.label}
                >
                  {item.icon}
                </a>
              ))}
            </div>
          </motion.div>

          {/* COLUMN 2: Quick Links (Updated & Ordered) */}
          <motion.div variants={FADE_UP} custom={1}>
            <SectionHeader>Quick Links</SectionHeader>
            <ul className="flex flex-col space-y-2.5 p-0 m-0 list-none">
              {QUICK_LINKS.map((link) => (
                <li key={link.label} className="p-0 m-0">
                  <a
                    href={link.href}
                    onClick={(e) => handleSmoothScroll(e, link.href)}
                    className="text-[#475569] hover:text-[#1976D2] text-sm font-medium transition-colors duration-200 inline-flex items-center gap-2 group cursor-pointer"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#1976D2]/30 group-hover:bg-[#1976D2] group-hover:scale-125 transition-all" />
                    <span>{link.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* COLUMN 3: Contact Details (New Address & Single Contact Number) */}
          <motion.div variants={FADE_UP} custom={2}>
            <SectionHeader>Reach Us</SectionHeader>
            <div className="space-y-4">
              {/* Address */}
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#EAF7FB] border border-[#2C61B924] text-[#1976D2] flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                  📍
                </span>
                <div>
                  <span className="block text-[0.7rem] uppercase tracking-wider font-bold text-[#64748B] mb-0.5">Head Office</span>
                  <p className="text-[#001726] font-semibold text-sm leading-snug m-0">
                    B-192, Sri Krishna Puri, Patna, Bihar 800001
                  </p>
                </div>
              </div>

              {/* Phone (Only one number) */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#EAF7FB] border border-[#2C61B924] text-[#1976D2] flex items-center justify-center text-sm flex-shrink-0">
                  📞
                </span>
                <div>
                  <span className="block text-[0.7rem] uppercase tracking-wider font-bold text-[#64748B] mb-0.5">Direct Line</span>
                  <a 
                    href="tel:9234804849" 
                    className="text-[#1976D2] hover:text-[#00B8D9] font-bold text-sm tracking-wide transition-colors"
                  >
                    +91 92348 04849
                  </a>
                </div>
              </div>

              {/* Email (All lowercase) */}
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 rounded-lg bg-[#EAF7FB] border border-[#2C61B924] text-[#1976D2] flex items-center justify-center text-sm flex-shrink-0">
                  ✉️
                </span>
                <div>
                  <span className="block text-[0.7rem] uppercase tracking-wider font-bold text-[#64748B] mb-0.5">Official Email</span>
                  <a 
                    href="mailto:arvihharinfo@gmail.com" 
                    className="text-[#1976D2] hover:text-[#00B8D9] font-semibold text-sm transition-colors break-all"
                  >
                    arvihharinfo@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* COLUMN 4: Google Map Widget */}
          <motion.div variants={FADE_UP} custom={3}>
            <SectionHeader>Our Location</SectionHeader>
            <div className="rounded-[18px] overflow-hidden border border-[#2C61B924] shadow-sm bg-white p-1">
              <iframe
                src="https://maps.google.com/maps?q=25.61145,85.11520&hl=en&z=19&output=embed"
                width="100%"
                height="150"
                style={{ border: 0, display: 'block', borderRadius: '14px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Aryavihhar Office Location - B-192 Sri Krishna Puri Patna"
              />
            </div>
            <a
              href="https://www.google.com/maps/search/?api=1&query=25.61145,85.11520"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold text-[#1976D2] hover:text-[#00B8D9] transition-colors"
            >
              <span>View On Google Maps →</span>
            </a>
          </motion.div>
        </motion.div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-[#2C61B924] to-transparent mb-7" />

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#64748B]">
          <p className="m-0 text-center sm:text-left">
            &copy; {new Date().getFullYear()} Aryavihhar Intelligence and Security Services Pvt. Ltd. All Rights Reserved.
          </p>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#16A34A]" />
            <span className="font-semibold text-[#1976D2] tracking-wider uppercase text-[10px]">
              B-192 Sri Krishna Puri · Patna · Bihar
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;